import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/api-error';
import { errorMessageFor } from '../auth/error-message';
import { communityApi as defaultApi } from './community-api';
import { PostCard } from './PostCard';
import './community.css';

const ROLE_LABELS = { admin: 'Administrador', coach: 'Profesor', user: 'Socio' };

function publicMediaUrl(path) {
  if (!path) return '';
  return path.startsWith('/media/') ? path : `/media/${path.replace(/^\/+/, '')}`;
}

export function ProfilePage({ api = defaultApi }) {
  const { userId } = useParams();
  const { user: viewer, refreshSession } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingFollow, setSavingFollow] = useState(false);
  const [error, setError] = useState('');
  const [aboutDraft, setAboutDraft] = useState('');
  const [aboutStatus, setAboutStatus] = useState('idle');
  const [photoStatus, setPhotoStatus] = useState('idle');
  const [photoError, setPhotoError] = useState('');
  const fileInputRef = useRef(null);
  const currentUserId = useRef(userId);
  currentUserId.current = userId;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setPhotoStatus('idle');
    setPhotoError('');
    setAboutStatus('idle');
    api.profile(userId)
      .then(result => { if (active) { setProfile(result); setAboutDraft(result.user?.aboutMe || ''); setAboutStatus('idle'); } })
      .catch(() => { if (active) setError('No se pudo cargar el perfil.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, userId]);

  async function toggleFollow() {
    if (savingFollow || !profile) return;
    setSavingFollow(true);
    setError('');
    try {
      if (profile.isFollowing) {
        await api.unfollow(userId);
        setProfile(current => ({ ...current, isFollowing: false }));
      } else {
        await api.follow(userId);
        setProfile(current => ({ ...current, isFollowing: true }));
      }
    } catch {
      setError('No se pudo actualizar el seguimiento.');
    } finally {
      setSavingFollow(false);
    }
  }

  async function saveAbout(event) {
    event.preventDefault();
    if (aboutStatus === 'saving') return;
    setAboutStatus('saving');
    setError('');
    try {
      const result = await api.setAbout(aboutDraft);
      setProfile(current => ({ ...current, user: { ...current.user, aboutMe: result?.aboutMe ?? null } }));
      setAboutDraft(result?.aboutMe || '');
      setAboutStatus('saved');
    } catch (caught) {
      setAboutStatus('idle');
      setError(errorMessageFor(caught));
    }
  }

  async function changePhoto(event) {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file || photoStatus === 'uploading') return;
    const startedFor = userId;
    setPhotoStatus('uploading');
    setPhotoError('');
    try {
      const avatarPath = await api.changeAvatar(file);
      // Refresca la sesión para que la foto nueva se vea en toda la app.
      try { await refreshSession(); } catch { /* la foto ya quedó guardada; la sesión se actualiza en la próxima carga */ }
      // Si mientras tanto se cambió de perfil, el resultado no se aplica al que se está viendo.
      if (currentUserId.current !== startedFor) return;
      setProfile(current => ({ ...current, user: { ...current.user, avatarPath } }));
      setPhotoStatus('done');
    } catch (caught) {
      if (currentUserId.current !== startedFor) return;
      setPhotoStatus('idle');
      // Se muestran tal cual los mensajes del servidor (ApiError) o los ya redactados en español por communityMedia;
      // cualquier otro error técnico (p. ej. "Failed to fetch") se reemplaza por un mensaje genérico.
      const friendly = caught instanceof ApiError || (typeof caught?.message === 'string' && caught.message.startsWith('No se pudo'));
      setPhotoError(friendly ? caught.message : 'No se pudo subir la foto. Revisá tu conexión e intentá de nuevo.');
    }
  }

  if (loading) return <section className="gym-page community-page"><p className="gym-state gym-state--loading" role="status">Cargando perfil…</p></section>;
  if (error && !profile) return <section className="gym-page community-page"><p className="gym-state gym-state--error" role="alert">{error}</p></section>;
  if (!profile) return null;

  const { user, posts = [] } = profile;
  const avatarUrl = publicMediaUrl(user.avatarPath);
  const isOwnProfile = viewer?.id === userId;

  return <section className="gym-page community-page" aria-labelledby="community-profile-title">
    <header className="community-profile">
      <div className="community-profile__avatar-wrap">
        {avatarUrl ? <img className="community-profile__avatar" src={avatarUrl} alt={`Foto de perfil de ${user.username}`} /> : <span className="community-profile__avatar community-profile__avatar--fallback" aria-hidden="true">{user.username?.charAt(0)?.toUpperCase() || '?'}</span>}
        {isOwnProfile && <>
          <input ref={fileInputRef} className="community-profile__file-input" type="file" accept="image/*" tabIndex={-1} aria-hidden="true" onChange={changePhoto} />
          <button type="button" className="gym-button gym-button--secondary" disabled={photoStatus === 'uploading'} onClick={() => fileInputRef.current && fileInputRef.current.click()}>Cambiar foto</button>
        </>}
      </div>
      <div className="community-profile__identity">
        <h1 id="community-profile-title" className="gym-page__title">{user.username}</h1>
        <p>{ROLE_LABELS[user.role] || 'Socio'}</p>
        {!isOwnProfile && user.aboutMe && <p className="community-profile__about">{user.aboutMe}</p>}
      </div>
      {!isOwnProfile && <button type="button" className={profile.isFollowing ? 'gym-button gym-button--secondary' : 'gym-button gym-button--primary'} onClick={toggleFollow} disabled={savingFollow}>{profile.isFollowing ? 'Dejar de seguir' : 'Seguir'}</button>}
    </header>
    {isOwnProfile && <p className="community-profile-edit__status" aria-live="polite">{photoStatus === 'uploading' ? 'Subiendo…' : photoStatus === 'done' ? 'Foto actualizada' : ''}</p>}
    {photoError && <p className="gym-form__error" role="alert">{photoError}</p>}
    {isOwnProfile && <form className="community-profile-edit" onSubmit={saveAbout}>
      <label className="gym-field__label" htmlFor="community-about-me">Sobre mí</label>
      <textarea className="gym-input" id="community-about-me" aria-describedby="community-about-count" maxLength={300} value={aboutDraft} onChange={event => { setAboutDraft(event.target.value); setAboutStatus('idle'); }} />
      <div className="community-profile-edit__row">
        <span id="community-about-count">{aboutDraft.length}/300</span>
        <span aria-live="polite">{aboutStatus === 'saving' ? 'Guardando…' : aboutStatus === 'saved' ? 'Guardado' : ''}</span>
        <button type="submit" className="gym-button gym-button--primary" disabled={aboutStatus === 'saving'}>Guardar</button>
      </div>
    </form>}
    {error && <p className="gym-form__error" role="alert">{error}</p>}
    <div className="community-profile__posts" aria-label={`Publicaciones de ${user.username}`}>
      {posts.map(post => <PostCard key={isOwnProfile ? `${post.id}:${user.avatarPath || ''}` : post.id} post={isOwnProfile && user.id && post.author?.id === user.id ? { ...post, author: { ...post.author, avatarPath: user.avatarPath } } : post} api={api} viewer={viewer} isAdmin={viewer?.role === 'admin'} />)}
      {!posts.length && <p className="gym-state gym-state--empty">Este perfil todavía no tiene publicaciones.</p>}
    </div>
  </section>;
}
